<template>
    <article class="prices">
        <!-- FAB Toggle Currency [XS SM MD] -->
        <v-fab-transition>
            <v-btn
                :key="getCurrency"
                color="blue"
                fab
                x-large
                dark
                bottom
                left
                fixed
                class="d-lg-none"
                @click="toggleCurrency"
            >
                <div class="d-flex flex-column">
                    <v-icon class=""> mdi-currency-usd</v-icon>{{ getAltCurrency }}
                </div>
            </v-btn>
        </v-fab-transition>

        <!-- Tarjetas Planes [XS SM MD] -->
        <article class="d-md-none" v-for="pack in formattedPacks" :key="pack.qty">
            <v-col cols="12">
                <PackToggler @toggler-click="toggleFeatured(pack.qty)" :toggled="pack.toggled" />

                <v-card class="pack-card">
                    <div class="pack-card__content">
                        <h2
                            class="pack-card__title"
                            :class="pack.toggled ? 'pack-card__title--toggled' : ''"
                        >
                            {{ pack.qty === 1 ? "Individual" : `Pack ${pack.qty}` }}
                        </h2>

                        <!-- Unit -->
                        <p class="pack-card__amount">
                            <span class="pack-card__amount--unit-symbol">$</span>
                            <span class="pack-card__amount--unit">{{
                                pack.formattedUnitPrice
                            }}</span>
                            <span class="pack-card__amount--info">
                                Por publicación
                            </span>
                        </p>

                        <!-- Savings -->
                        <p v-if="pack.qty > 1 && !pack.toggled" class="pack-card__savings">
                            (Ahorra un {{ pack.savings }}%)
                        </p>

                        <!-- Total -->
                        <p v-if="pack.qty > 1" class="pack-card__amount">
                            <span class="pack-card__amount--total-symbol">$</span>
                            <span class="pack-card__amount--total">{{
                                pack.formattedTotalPrice
                            }}</span>
                            <span class="pack-card__amount--info">
                                Valor final
                            </span>
                        </p>

                        <!-- Perks -->
                        <div class="pack-card__perks">
                            <div>
                                • {{ pack.qty }}
                                {{ pack.qty === 1 ? "publicación" : "publicaciones" }}
                            </div>
                            <div v-if="currency === 1">
                                <div>
                                    • Precios (CLP) + IVA (19%)
                                </div>
                                <div v-if="pack.toggled">• <strong>3x</strong> de visibilidad</div>
                                <div v-if="pack.toggled">
                                    • <strong>Más publicaciones</strong> en RRSS y Newsletter
                                </div>
                            </div>
                        </div>
                    </div>

                    <v-btn class="pack-card__btn" @click="onCheckoutClick(pack)">
                        {{ context === "pricing" ? "CONTRATA" : "PAGA" }} AQUÍ
                    </v-btn>
                </v-card>
            </v-col>
        </article>

        <!-- Última tarjeta -->
        <article class="d-md-none">
            <v-card class="pack-card pack-card--last d-flex flex-column justify-center">
                <h2 class="pack-card__title pack-card__title--last">
                    Pack Corporate
                </h2>
                <p class="pack-card__text pack-card__text--last">
                    ¿Necesitas más de 10 publicaciones? <br />
                    Conversemos
                </p>

                <v-btn class="pack-card__btn" @click="displayModal()">
                    CONTÁCTANOS
                </v-btn>
            </v-card>
        </article>
        <ContactModal :isOpen="isDisplayModal" @close="isDisplayModal = false"/>

        <!-- Tabla Planes [LG XL] -->
        <v-container class="d-none d-md-block">
            <v-row justify="end">
                <!-- Currency toggle -->
                <div class="currency">
                    <v-btn-toggle v-model="currency" mandatory borderless dense>
                        <v-btn
                            class="currency__btn currency__btn--left"
                            active-class="currency__btn--active"
                        >
                            USD
                        </v-btn>
                        <v-btn
                            class="currency__btn currency__btn--right"
                            active-class="currency__btn--active"
                        >
                            CLP
                        </v-btn>
                    </v-btn-toggle>
                </div>
            </v-row>

            <v-row justify="center">
                <v-col cols="12">
                    <table class="table">
                        <!-- Encabezado -->
                        <thead>
                            <tr class="head-title">
                                <th class="head-title__featured d-flex">
                                    CAMBIA A DESTACADO
                                    <v-switch
                                        v-model="featuredToggle"
                                        class="ml-2"
                                        color="orange"
                                    />
                                </th>
                                <th class="head-title__outer rounded-l-lg">
                                    <strong>INDIVIDUAL</strong>
                                </th>
                                <th colspan="3" class="head-title__center rounded-r-lg">
                                    <strong>PACKS (ahorra)</strong>
                                </th>
                            </tr>

                            <tr class="table__separator">
                                <th colspan="4"></th>
                            </tr>
                            <tr class="title-table">
                                <th class="title-table__title-detail rounded-l-lg">
                                    CANTIDAD PUBLICACIONES
                                </th>
                                <th class="title-table__title">1</th>
                                <th class="title-table__title">3</th>
                                <th class="title-table__title">5</th>
                                <th class="title-table__title rounded-r-lg">10</th>
                            </tr>
                        </thead>

                        <!-- Cuerpo -->
                        <tbody>
                            <tr class="body">
                                <td
                                    class="py-9 pl-2 body__detail-column d-flex flex-column align-left"
                                >
                                    <span class="grow"
                                        >Precio unitario por publicación ({{ getCurrency }})*</span
                                    >
                                    <span>Precio ({{ getCurrency }})*</span>
                                    <span>Ahorro por publicación</span>
                                    <p v-if="currency === 1">*Precios + IVA (19%)</p>
                                </td>
                                <td
                                    class="body__pricing py-9"
                                    v-for="pack in formattedPacks"
                                    :key="pack.qty"
                                >
                                    <span
                                        class="body__pricing__unit d-flex justify-center align-center"
                                    >
                                        {{ pack.formattedUnitPrice }}
                                        <span class="body__pricing__unit--small">
                                            {{ getCurrency === "CLP" ? "UF" : "USD" }}
                                        </span>
                                    </span>
                                    <span
                                        class="body__pricing__total d-flex justify-center align-center"
                                    >
                                        <strong>
                                            {{ pack.formattedTotalPrice }}{{ getCurrency === "CLP" ? "UF" : "USD"
                                            }}
                                        </strong>
                                    </span>
                                    <span
                                        class="body__pricing__save d-flex justify-center align-start"
                                    >
                                        <div
                                            class="saved d-flex justify-center align-center"
                                            v-if="pack.qty !== 1"
                                        >
                                            {{ pack.savings }} %
                                        </div>
                                    </span>

                                    <v-btn
                                        class="body__pricing__btn"
                                        dark
                                        color="#3badff"
                                        @click="onCheckoutClick(pack)"
                                        elevation="0"
                                    >
                                        ¡{{ context === "pricing" ? "CONTRATA" : "PAGA" }} AQUÍ!
                                    </v-btn>
                                </td>
                            </tr>
                            <tr
                                v-for="description in descriptions"
                                :key="description"
                                class="description"
                            >
                                <td class="text-start py-2 px-2">
                                    <span v-html="description"></span>
                                </td>
                                <td class="text-center" v-for="count in 4" :key="count">
                                    <v-icon color="#00Af4A">mdi-check-bold</v-icon>
                                </td>
                            </tr>
                            <tr
                                v-for="description in featuredDescriptions"
                                :key="description"
                                class="description"
                            >
                                <td class="text-start py-2 px-2">
                                    <span
                                        class="description__text"
                                        :class="{ 'description__text--grayed': !featuredToggle }"
                                    >
                                        {{ description }}
                                    </span>
                                </td>
                                <td class="text-center" v-for="count in 4" :key="count">
                                    <v-icon :color="featuredToggle ? '#FF8E03' : '#B9B8B8'">
                                        mdi-{{ featuredToggle ? "check-bold" : "minus-thick" }}
                                    </v-icon>
                                </td>
                            </tr>
                            <span class="disclaimer-time d-flex justify-start mt-4 ml-2">
                                * Duración por pack: 1 año.
                            </span>
                        </tbody>
                    </table>
                </v-col>
            </v-row>
        </v-container>

        <!-- Dialogo Advertencia Persona -->
        <v-dialog class="prices__dialog" v-model="personWarningDialog" max-width="500px">
            <v-card class="text-center">
                <v-card-title class="primary justify-center">
                    <h5 class="prices__dialog-title white--text">Lo sentimos</h5>
                </v-card-title>

                <v-card-text class="prices__dialog-text pt-6 black--text">
                    Solo puedes comprar si te identificas como organización.
                </v-card-text>

                <v-divider />

                <v-card-actions class="prices__dialog-actions">
                    <v-btn
                        class="prices__dialog-btn"
                        color="primary"
                        @click="personWarningDialog = false"
                    >
                        ENTIENDO
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </article>
</template>

<script>
import PackToggler from "@/components/PackToggler";
import { mapMutations, mapGetters } from "vuex";
import { packs } from "@/config/fixtures";
import ContactModal from "@/components/ContactModal";

export default {
    name: "Prices",

    components: {
        PackToggler,
        ContactModal,
    },

    props: {
        context: {
            type: String,
            required: true,
            default: "pricing" // 'services' o 'dashboard'
        }
    },

    data() {
        return {
            currency: 1,
            descriptions: [
                "Acceso a una comunidad de +200.000 talentos en búsqueda de Trabajos con Sentido",
                "ATS (gestiona tu proceso en nuestra plataforma)",
                "Hasta 60 días de publicación (consigue más candidatos/as)",
                "Crea tu perfil de organización gratis (haz visible tu organización a toda nuestra comunidad)"
            ],
            featuredDescriptions: [
                "Tu oferta en la sección destacados, donde hay hasta 3x más visibilidad (por hasta 30 días)",
                "Una publicación en nuestras RRSS asegurada (Linkedin: 200.000+ seguidores)",
                "Una salida en nuestro newsletter asegurada (+140.000 inscritos)"
            ],
            featuredToggle: false,
            personWarningDialog: false,
            uf: 0,
            isDisplayModal: false,
        };
    },

    computed: {
        ...mapGetters("users", ["getType"]),

        basePrice() {
            return this.packs[0].price;
        },
        formattedPacks() {
            return this.packs.map(({ name, price, qty, packCode, toggled }) => {
                let unitPrice = this.currency === 0 ? price.usd : price.clp;
                let totalPrice = Math.round(unitPrice * qty);

                return {
                    currencyCode: this.currency === 0 ? "USD" : "CLP",
                    formattedTotalPrice: this.formatCurrency(totalPrice, this.currency),
                    formattedUnitPrice: this.formatCurrency(unitPrice, this.currency),
                    name,
                    packCode,
                    qty,
                    savings: this.calculateSavings(unitPrice, toggled || this.featuredToggle),
                    toggled,
                    totalPrice,
                    unitPrice
                };
            });
        },
        contactUs() {
            return "mailto:hola@trabajoconsentido.com?subject=Quiero contratar más de 10 publicaciones";
        },
        getCurrency() {
            return this.currency === 0 ? "USD" : "CLP";
        },
        getAltCurrency() {
            return this.currency === 0 ? "CLP" : "USD";
        },
        packs() {
            return packs
                .filter(p => p.featured === this.featuredToggle)
                .map(p => ({ ...p, toggled: false }));
        }
    },

    methods: {
        ...mapMutations("payments", ["SET_CURRENCY", "SET_SELECTED_PACK"]),

        calculateSavings(price, toggled) {
            let basePrice = this.currency === 0 ? this.basePrice.usd : this.basePrice.clp;

            let savings = ((basePrice - price) / basePrice) * 100;

            return Math.round(savings);
        },
        formatCurrency(amount, currency) {
            let usdFormatter = new Intl.NumberFormat("en-US", {
                style: "decimal",
                currency: "USD",
                currencyDisplay: "code"
            });
            let clpFormatter = new Intl.NumberFormat("es-CL", {
                style: "decimal",
                currency: "CLP"
            });

            return currency === 0 ? usdFormatter.format(amount) : clpFormatter.format(amount);
        },
        onCheckoutClick(selectedPack) {
            // Si tipo usuario candidato, advertir
            if (this.getType === "candidate") {
                this.personWarningDialog = true;
                return;
            }

            selectedPack = {
                ...selectedPack,
                packType: this.featuredToggle ? "featured" : "normal"
            };
            this.SET_CURRENCY(this.getCurrency);
            this.SET_SELECTED_PACK(selectedPack);
            // CAMBIAR LOGICA
            if (!localStorage.getItem("user"))
                localStorage.setItem(
                    "checkout_params",
                    JSON.stringify({ pack: selectedPack, currency: this.getCurrency })
                );
            //this.$router.push("/checkout"); // agregar ruta de pago

            window.location.href = `mailto:${process.env.VUE_APP_PAYMENT_MAIL}`;
        },
        toggleCurrency() {
            if (this.currency === 0) this.currency = 1;
            else if (this.currency === 1) this.currency = 0;
        },
        toggleFeatured(qty) {
            let index = this.packs.findIndex(p => p.qty === qty);
            this.packs[index].toggled = !this.packs[index].toggled;
        },
        displayModal() {
            this.isDisplayModal = true;
            console.log(this.isDisplayModal);
        },
    }
};
</script>
